import clsx from "clsx";
import { RefObject, useRef } from "react";

import {
  Button,
  Displayer,
  Icon,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import {
  AutoFillStatus,
  IntakeFormSectionRef,
  sectionFallback,
  SectionItem,
  summaryTitle,
} from "../../types";

export type SectionSidebarProps = {
  autoFillSection?: SectionItem;
  visibleSections: SectionItem[];
  selected: SectionItem | undefined;
  onSelect: (section: SectionItem) => void;
  summary: boolean;
  onSummary: () => void;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  sectionRef?: RefObject<IntakeFormSectionRef>;
  isUpdating?: boolean;
  isAllVisited?: boolean;
  isNavigatable: (section: SectionItem) => boolean;
  isSectionVisiting: (section: SectionItem) => boolean;
  isAutoFillSectionSelected?: boolean;
  autoFillStatus?: AutoFillStatus;
};

export const SectionSidebar = ({
  autoFillSection,
  visibleSections,
  selected,
  onSelect,
  summary,
  onSummary,
  sidebar,
  setSidebar,
  sectionRef,
  isUpdating,
  isAllVisited,
  isNavigatable,
  isSectionVisiting,
  isAutoFillSectionSelected,
  autoFillStatus,
}: SectionSidebarProps) => {
  const backgroundRef = useRef<HTMLButtonElement>(null);
  const backgroundIsVisible = () =>
    backgroundRef.current &&
    window.getComputedStyle(backgroundRef.current).display === "block";
  const selectedIndex = selected
    ? visibleSections.findIndex((s) => s.uri === selected?.uri)
    : -1;

  const submitVisitingSection = (options?: { skip?: boolean }): boolean => {
    if (options?.skip) return true;

    if (sectionRef && selected && isSectionVisiting(selected))
      sectionRef.current?.submitForm();

    return (
      !sectionRef?.current?.errors &&
      sectionRef?.current?.connection?.status !== "error"
    );
  };

  const getNavItemClassNames = ({ isSelected }: { isSelected: boolean }) =>
    clsx(
      "flex justify-between items-center rounded whitespace-nowrap overflow-hidden text-ellipsis",
      "px-[1.6rem] py-[1rem] text-left w-full text-ui font-semibold",
      "disabled:cursor-not-allowed disabled:text-neutral-400",
      isSelected
        ? "bg-cyan-140 text-neutral-850"
        : "bg-white text-neutral-600 hover:bg-neutral-50",
    );
  const sidebarClassNames = clsx(
    "flex flex-col gap-small z-90 absolute bottom-0 rounded-t shadow-modal w-full min-h-[25%] max-h-[85%]",
    "@tablet:shadow-none @tablet:max-h-full @tablet:max-h-none @tablet:absolute @tablet:top-[7rem] @tablet:rounded-none @tablet:w-[30rem]",
    "border-r border-neutral-100 bg-white transition-left duration-300 ease",
    sidebar
      ? "translate-y-0 @tablet:left-0"
      : "translate-y-full @tablet:translate-y-0 @tablet:left-[-28rem]",
  );

  return (
    <>
      <button
        ref={backgroundRef}
        className={clsx(
          "z-[89] absolute top-0 bottom-0 right-0 bg-black border-none p-0 @tablet:hidden cursor-default",
          sidebar
            ? "[transition:opacity_0.3s_ease,width_0.4s_steps(1,jump-start)] w-full opacity-40"
            : "[transition:opacity_0.3s_ease,width_0.4s_steps(1,jump-end)] w-0 opacity-0",
        )}
        type="button"
        onClick={() => setSidebar(false)}
        aria-disabled={!sidebar}
      />
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <nav aria-disabled={!sidebar} className={sidebarClassNames}>
        <Button
          className={clsx(
            "absolute top-[5.6rem] right-[-2rem] transition-transform duration-300 ease hidden @tablet:block",
            sidebar ? "rotate-180" : "rotate-0",
          )}
          leftIcon="solidChevronRight"
          variant="secondaryDefault"
          rounded
          onClick={() => setSidebar(!sidebar)}
          title={sidebar ? "Close Menu" : "Open Menu"}
        />
        <h3
          className={clsx(
            "text-paragraph font-semibold px-[2.2rem] py-[2rem] border-b border-neutral-100",
            "@tablet:border-none @tablet:px-[3rem] @tablet:py-[2rem] @tablet:mt-[5.6rem]",
          )}
        >
          Sections
        </h3>
        <div
          className={clsx(
            "flex flex-col gap-small flex-1 overflow-x-hidden px-[0.8rem] @tablet:px-[1.6rem] pb-[1rem]",
            sidebar ? "overflow-y-auto" : "overflow-y-hidden",
          )}
        >
          {autoFillSection && (
            <div>
              <button
                type="button"
                role="link"
                className={getNavItemClassNames({
                  isSelected: !!isAutoFillSectionSelected,
                })}
                onClick={() => {
                  if (backgroundIsVisible()) setSidebar(false);
                  if (
                    (summary || !isAutoFillSectionSelected) &&
                    submitVisitingSection({
                      skip: !isUpdating || visibleSections.length === 1,
                    })
                  )
                    onSelect(autoFillSection);
                }}
                aria-current={isAutoFillSectionSelected}
                disabled={autoFillStatus === "filling"}
              >
                <div className="flex items-center gap-small flex-1">
                  <span className="truncate">{autoFillSection.title}</span>
                  <Icon name="archie" />
                </div>
                {isAutoFillSectionSelected && <Icon name="regularArrowRight" />}
              </button>
            </div>
          )}
          {visibleSections.map((section, index) => {
            const isSelected = !summary && index === selectedIndex;
            const isDisabled = !summary && !isNavigatable(section);
            return (
              <Tooltip
                text={
                  isDisabled
                    ? "You must complete the required fields before continuing"
                    : undefined
                }
                placement="bottom-end"
                offsetBy={8}
                key={section.uri}
              >
                <button
                  key={section.uri}
                  type="button"
                  role="link"
                  className={getNavItemClassNames({
                    isSelected,
                  })}
                  onClick={() => {
                    if (backgroundIsVisible()) setSidebar(false);

                    if (
                      (summary || section.uri !== selected?.uri) &&
                      submitVisitingSection({
                        skip: !isUpdating && index < selectedIndex,
                      })
                    )
                      onSelect(section);
                  }}
                  aria-current={isSelected}
                  disabled={isDisabled}
                >
                  <div className="flex-1 truncate">
                    <Displayer
                      value={section.title}
                      paragraphTag="span"
                      fallback={sectionFallback}
                    />
                  </div>
                  {!summary && index === selectedIndex && (
                    <Icon name="regularArrowRight" />
                  )}
                </button>
              </Tooltip>
            );
          })}
          {visibleSections.length > 1 && (
            <Tooltip
              text={
                !isAllVisited
                  ? "You must complete the required fields before continuing"
                  : undefined
              }
              placement="bottom-end"
              offsetBy={8}
            >
              <button
                type="button"
                role="link"
                className={getNavItemClassNames({
                  isSelected: !!summary,
                })}
                onClick={() => {
                  if (backgroundIsVisible()) setSidebar(false);
                  if (!summary && submitVisitingSection()) onSummary();
                }}
                aria-current={!!summary}
                disabled={!isAllVisited}
              >
                <span className="flex-1 truncate">{summaryTitle}</span>
                {!!summary && <Icon name="regularArrowRight" />}
              </button>
            </Tooltip>
          )}
        </div>
      </nav>
    </>
  );
};
